import { autoUpdate, offset, useClick, useFloating, useInteractions } from '@floating-ui/react';
import debounce from 'lodash.debounce';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import { SPACE_100 } from '@alltrails/denali/tokens';
import SignUpModal from '@alltrails/user-auth/components/SignUpModal';
import useStyleKeyboardShortcuts from '../../hooks/useStyleKeyboardShortcuts';
import useStyleSelection from '../../hooks/useStyleSelection';
import { type RootState, useAppSelector } from '../../redux';
import type { StyleId } from '../../types/Styles';
import LayerButton from '../LayerButton/LayerButton';
import filterHiddenStyles from '../LayerMenu/filterHiddenStyles';
import GridLayerMenu from '../LayerMenu/GridLayerMenu';

type StyleButtonProps = {
  /**
   * Restrict the layer menu and its keyboard shortcuts to these style ids. Entitlement, admin, PLP and
   * 3D gating still apply on top, so this can only remove styles. Omit to offer every style; `[]` offers
   * none, so pass `undefined` rather than an empty array while an async list is still resolving.
   */
  allowedStyleIds?: StyleId[];
  /**
   * Open the layer menu clear of the button instead of tucked over it. The default placement pulls the
   * menu back onto the trigger, which reads as the menu covering the button on surfaces where it opens
   * against a short control stack.
   */
  avoidTriggerOverlap?: boolean;
  /**
   * Force the layer button to render its compact CTA regardless of viewport width. See `LayerButton`.
   */
  forceCompact?: boolean;
  isStyleModalInitiallyOpen?: boolean;
  onStyleModalChange?: (isOpen: boolean) => void;
} & Omit<Parameters<typeof useStyleSelection>[0], 'handleRoadblock'> &
  Pick<ComponentProps<typeof GridLayerMenu>, 'mapLocation' | 'pauseFocusTrap'>;

const StyleButton = ({
  allowedStyleIds,
  avoidTriggerOverlap = false,
  forceCompact = false,
  disableAnalytics,
  isStyleModalInitiallyOpen = false,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  onStyleModalChange,
  handleFailedEntitlementCheck,
  mapLocation,
  pauseFocusTrap
}: StyleButtonProps) => {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [styleIdAfterRoadblock, setStyleIdAfterRoadblock] = useState<StyleId | undefined>(undefined);
  const user = useUser();

  const handleRoadblock = useCallback(
    (styleId?: StyleId) => {
      if (!user) {
        setIsSignupModalOpen(true);
        setStyleIdAfterRoadblock(styleId);
      }
    },
    [user]
  );

  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const isPLP = hasPermission({ permission: 'public_lands:manage' });
  const { is3dActive, supportedDynamicStyleIds } = useAppSelector((state: RootState) => ({
    is3dActive: state.map.is3dActive,
    supportedDynamicStyleIds: state.map.supportedDynamicStyleIds
  }));

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });

  const onShortcutStyleSelect = useCallback(
    (styleId: StyleId) => {
      const isVisibleHere = filterHiddenStyles({ allowedStyleIds, ids: [styleId], isAdmin, isPLP, is3dActive, supportedDynamicStyleIds }).length > 0;
      if (!isVisibleHere) {
        return;
      }
      onStyleSelect(styleId);
    },
    [allowedStyleIds, is3dActive, isAdmin, isPLP, onStyleSelect, supportedDynamicStyleIds]
  );

  const [isStyleModalOpen, setIsStyleModalOpen] = useState(isStyleModalInitiallyOpen);

  // Admins get these without having to open the modal. Everyone else gets them only while it is
  // open, which is where the menu's own cards used to register them.
  useStyleKeyboardShortcuts(isAdmin || isStyleModalOpen ? onShortcutStyleSelect : undefined);

  // Debounced to handle race conditions when clicking on the layer button while the layer menu is already open.
  // This can close the menu via focus trap deactivation before the button has a chance to handle the click, which opens it again.
  const updateModalStatus = useMemo(
    () =>
      debounce((isOpen: boolean) => {
        onStyleModalChange?.(isOpen);
        setIsStyleModalOpen(isOpen);
      }, 50),
    [onStyleModalChange]
  );

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-end',
    middleware: [avoidTriggerOverlap ? offset({ mainAxis: SPACE_100, crossAxis: 0 }) : offset(({ rects }) => -rects.reference.height)],
    whileElementsMounted: autoUpdate
  });

  const click = useClick(context);

  const { getFloatingProps } = useInteractions([click]);

  useEffect(() => {
    if (user && styleIdAfterRoadblock) {
      onStyleSelect(styleIdAfterRoadblock);
      setStyleIdAfterRoadblock(undefined);
    }
  }, [onStyleSelect, styleIdAfterRoadblock, user]);

  return (
    <>
      <LayerButton forceCompact={forceCompact} isOpen={isStyleModalOpen} onClick={updateModalStatus} ref={refs.setReference} />
      {isStyleModalOpen && (
        <GridLayerMenu
          allowedStyleIds={allowedStyleIds}
          desktopModalProps={{ ...getFloatingProps(), style: floatingStyles }}
          disableAnalytics={disableAnalytics}
          handleFailedEntitlementCheck={handleFailedEntitlementCheck}
          handleRoadblock={handleRoadblock}
          isOpen={isStyleModalOpen}
          mapLocation={mapLocation}
          onCloseRequest={() => {
            updateModalStatus(false);
          }}
          onMapBaseLayerTapped={onMapBaseLayerTapped}
          onMapOverlayTapped={onMapOverlayTapped}
          pauseFocusTrap={pauseFocusTrap}
          ref={refs.setFloating}
        />
      )}
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => {
            setIsSignupModalOpen(false);
          }}
          isOpen={isSignupModalOpen}
          onSuccess={() => {
            setIsSignupModalOpen(false);
          }}
          trigger="Select Map Overlay"
          upsellTrigger="Select Map Overlay"
        />
      )}
    </>
  );
};

export default StyleButton;
